/**
 * # Eurail Product Summary
 *
 *
 */
import { ElementsSDK } from '@yiluhub/ui-sdk-react';
import { useIsDesktop } from '@yiluhub/ui-utilities';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { getCurrentLanguage } from 'utils/i18n';

import TermsCheck from 'components/TermsCheck';

import { getTravelDaysLabel, getTravelDurationLabel } from 'modules/eurail/utils/getProductDetails';
import { getLocationLabel } from 'modules/eurail/utils/locationData';
import { PassType, getFareLabel } from 'modules/eurail/utils/types';
import { generateGuestsCountText } from 'modules/lounges/utils/getProductDetails';

import './styles.scss';
import { EurailProductSummaryProps } from './types';

export const ERROR_PRICE = 999999;

export const EurailProductSummary: React.FC<EurailProductSummaryProps> = ({
  fareClass,
  adult,
  youth,
  child,
  senior,
  pass = PassType.GLOBAL,
  country,
  price = ERROR_PRICE,
  currency,
  numberOfTravelDays,
  validityPeriodMonth,
  showSelection = true,
  isLoading,
  isDisabled,
  onClick,
  termsDetails,
  ...props
}) => {
  const { t } = useTranslation();
  const currentLanguage = getCurrentLanguage();
  const { isDesktop } = useIsDesktop();

  const guestsFieldText = generateGuestsCountText({ adult, child, youth, senior }, t, 'eurail');
  const travelDays = getTravelDaysLabel(t, numberOfTravelDays);
  const travelDuration = getTravelDurationLabel(t, validityPeriodMonth);

  const extendedView = (
    <>
      <ElementsSDK.Typography className="yilu-ProductSummary__Title" variant="h6" bold uppercase>
        {t('productSummary.your.selection.label')}
      </ElementsSDK.Typography>
      <div className="yilu-ProductSummary__SubTitle">
        <ElementsSDK.Typography variant="h5" bold>
          {pass === PassType.ONE_COUNTRY
            ? t('eurail.product.summary.country.title', {
                country: getLocationLabel(country, currentLanguage),
              })
            : t('eurail.product.summary.global.title')}
        </ElementsSDK.Typography>
      </div>
      {isDesktop && (
        <div className="yilu-ProductSummary__Itinerary">
          <div className="yilu-ProductSummary__List" data-testid="timeline-ol">
            <div className="yilu-ProductSummary__List__Item">
              <ElementsSDK.Typography
                variant="p2"
                className={'yilu-ProductSummary__ItineraryLabel'}
              >
                {t('eurail.product.summary.pass.details.title')}
              </ElementsSDK.Typography>
              <ElementsSDK.Typography variant="p1" bold>
                {travelDays} {travelDuration}
              </ElementsSDK.Typography>
            </div>
            <div className="yilu-ProductSummary__List__Item">
              <ElementsSDK.Typography
                variant="p2"
                className={'yilu-ProductSummary__ItineraryLabel'}
              >
                {t('eurail.product.summary.class.fare.title')}
              </ElementsSDK.Typography>
              <ElementsSDK.Typography variant="p1" bold>
                {getFareLabel(t, fareClass)}
              </ElementsSDK.Typography>
            </div>
            <div className="yilu-ProductSummary__List__Item">
              <ElementsSDK.Typography
                variant="p2"
                className={'yilu-ProductSummary__ItineraryLabel'}
              >
                {t('eurail.product.summary.passengers.title')}
              </ElementsSDK.Typography>
              <ElementsSDK.Typography variant="p1" bold>
                {guestsFieldText}
              </ElementsSDK.Typography>
            </div>
          </div>
        </div>
      )}
      {!isDesktop && (
        <div className={'ProductSummary__Itinerary--mobile'}>
          <ElementsSDK.Typography variant="p2" bold>
            {travelDays} {travelDuration}
          </ElementsSDK.Typography>
          <ElementsSDK.Typography variant="p2">{getFareLabel(t, fareClass)}</ElementsSDK.Typography>
          <ElementsSDK.Typography variant="p2">{guestsFieldText}</ElementsSDK.Typography>
        </div>
      )}
    </>
  );

  return (
    <div className={clsx(showSelection && 'yilu-ProductSummaryEurail')}>
      {showSelection && extendedView}
      <ElementsSDK.PriceSummary
        className={clsx('yilu-ProductSummaryEurail__PriceSummary', {
          'yilu-ProductSummaryEurail__PriceSummary--footer': !isDesktop,
        })}
        price={price}
        currency={currency}
        isLoading={isLoading}
        isDisabled={isDisabled}
        {...props}
        onSubmit={onClick}
        termsComponent={
          termsDetails && (
            <TermsCheck
              termsUrl={termsDetails?.termsUrl || ''}
              productDetails={termsDetails?.productDetails}
            />
          )
        }
        isTotalHighlighted
        priceTypographySize="extraLarge"
      />
    </div>
  );
};
